import { pages } from "@microsoft/teams-js";
import { ButtonCircle } from "@momentum-ui/react-collaboration";
import { IWebexIntContact } from "@webex/component-adapter-interfaces/dist/esm/src";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TabBar, TabBarItem, WEBEX_TAB_ID } from "../components/TabBar";
import config from "../config";
import { useWebexUserId } from "../hooks/useWebexUserId";
import {
  getBusinessNumber,
  getWebexTokens,
  WebexTokenEnum,
} from "../ms-teams-sdk/service/MsTeamsAuthenticationService";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
} from "../ms-teams-sdk/util/LocalStorageUtil";
import { getMSTeamsContext } from "../ms-teams-sdk/util/MsTeamsContextUtil";
import { callVoicemailURL } from "../ms-teams-sdk/util/TobarUtils";
import {
  CH_STORAGE_KEYS,
  IBusinessNumberResponse,
  pickDisplayBusinessPhone,
  VM_STORAGE_KEYS,
} from "../packages/adapter-interfaces/src";
import { formatError, Logger } from "../packages/logger/src";
import {
  IWebexCallHistoryHandle,
  IWebexVoicemailHandle,
  WebexCallHistory,
  IWebexMobileDialpadHandle,
  WebexMobileDialpad,
  WebexVoicemail,
} from "../packages/widgets/src";
import { initWebex, whenDeviceRegistered } from "../packages/widgets/src/services/WebexInstanceService";
import { submitMetrics } from "../packages/widgets/src/services/WebexMetricsService";
import { invalidateVoicemailClient } from "../packages/widgets/src/services/WebexVoicemailService";
import { IChannelData } from "../packages/widgets/src/utils/CallBackUrl";
import { entityId, subPageId } from "../packages/widgets/src/utils/commonUtils";
import {
  audioCall,
  crossLaunchCallMetric,
  LOCALIZATION_CODES,
  MobileDialpad,
  videoCall,
} from "../packages/widgets/src/utils/MetricUtils";
import { buildMobileCallingUrl } from "../packages/widgets/src/utils/mobileCallingUrl";
import { isMobileFeatureParityEnabledForCurrentUser } from "../packages/widgets/src/utils/mobileFeatureParityConfig";
import { CommonErrorAlert } from "./Alerts/CommonErrorAlert";
import "./MobileLandingPage.scss";

const MobileKeypadCloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="currentColor"
      d="M6.22 5.16 5.16 6.22 10.94 12l-5.78 5.78 1.06 1.06L12 13.06l5.78 5.78 1.06-1.06L13.06 12l5.78-5.78-1.06-1.06L12 10.94 6.22 5.16Z"
    />
  </svg>
);

const MobileKeypadKeyboardIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="currentColor"
      d="M4.5 6.75A1.75 1.75 0 0 0 2.75 8.5v7c0 .97.78 1.75 1.75 1.75h15c.97 0 1.75-.78 1.75-1.75v-7c0-.97-.78-1.75-1.75-1.75h-15Zm0 1.5h15c.14 0 .25.11.25.25v7c0 .14-.11.25-.25.25h-15a.25.25 0 0 1-.25-.25v-7c0-.14.11-.25.25-.25ZM6 10.25h1.5V11.75H6v-1.5Zm3 0h1.5V11.75H9v-1.5Zm3 0h1.5V11.75H12v-1.5Zm3 0h1.5V11.75H15v-1.5Zm3 0H19.5V11.75H18v-1.5ZM6 13.25h12v1.5H6v-1.5Z"
    />
  </svg>
);

const MobileKeypadToggleIcon = () => (
  <svg
    viewBox="0 0 32 32"
    width="16"
    height="16"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="currentColor"
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5m0 7a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5m8-7a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5m0 7a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5m-8 7a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5m8 0a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5m0 7a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5m8-21a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5m0 7a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5m0 7a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5"
    />
  </svg>
);

const MAIN_TAB_SOURCE = "main";

/**
 * Mobile calling home. Allow-listed orgs on the main tab get search
 * above Recents, Missed, and Voicemail. The number pad opens from the
 * keypad icon. The open pad matches the Figma chrome: close, Type or
 * Dial search, keyboard, and My number under the call buttons. Other orgs and
 * chat-extension stay dialpad-only.
 *
 * @param root0
 * @param root0.src
 * @param root0.user
 * @param root0.channelData
 * @param root0.clientType
 */
export const MobileLandingPage = ({
  src,
  user = undefined,
  channelData = undefined,
  clientType,
}: {
  src: string;
  user?: IWebexIntContact[];
  channelData?: IChannelData;
  clientType: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const showFeatureTabs =
    src === MAIN_TAB_SOURCE && isMobileFeatureParityEnabledForCurrentUser();
  const webexUserId = useWebexUserId(showFeatureTabs);
  const [myNumber, setMyNumber] = useState("");
  const [unreadMissedCalls, setUnreadMissedCalls] = useState<number>();
  const [unreadVoicemails, setUnreadVoicemails] = useState<number>();
  const [isShowVMTab, setIsShowVMTab] = useState(true);
  const [isLocaleGerman, setIsLocaleGerman] = useState(false);
  const [isVmPlayErrorMsg, setIsVmPlayErrorMsg] = useState(false);
  const [vmListErrMsg, setVmListErrMsg] = useState("");
  const [chListErrMsg, setChListErrMsg] = useState("");
  const [isKeypadOpen, setIsKeypadOpen] = useState(false);
  const [isRecentsDetailOpen, setIsRecentsDetailOpen] = useState(false);
  const [isMissedDetailOpen, setIsMissedDetailOpen] = useState(false);
  const [isVoicemailDetailOpen, setIsVoicemailDetailOpen] = useState(false);
  const [keypadSlot, setKeypadSlot] = useState<HTMLDivElement | null>(null);
  const isRecordDetailOpen =
    isRecentsDetailOpen || isMissedDetailOpen || isVoicemailDetailOpen;
  const callHistoryRef = useRef<IWebexCallHistoryHandle>(null);
  const missedCallHistoryRef = useRef<IWebexCallHistoryHandle>(null);
  const voicemailRef = useRef<IWebexVoicemailHandle>(null);
  const dialpadRef = useRef<IWebexMobileDialpadHandle>(null);
  const suppressKeypadOpenUntilRef = useRef(0);
  const tabBarContainerRef = useRef<HTMLElement>(null);
  const reInitInFlightRef = useRef(false);
  const lastReInitAtRef = useRef(0);
  const MIN_REINIT_INTERVAL_MS = 60_000;
  const focusActiveFeatureTab = useCallback(() => {
    tabBarContainerRef.current
      ?.querySelector<HTMLButtonElement>(
        ".tab-bar--mobile-pill .tab-bar__tab--active"
      )
      ?.focus();
  }, []);
  const wasKeypadOpenRef = useRef(false);
  const closeKeypad = useCallback(() => {
    dialpadRef.current?.clearSearch();
    setIsKeypadOpen(false);
  }, []);

  useEffect(() => {
    if (isKeypadOpen) {
      wasKeypadOpenRef.current = true;
      return undefined;
    }
    if (!wasKeypadOpenRef.current) {
      return undefined;
    }
    wasKeypadOpenRef.current = false;
    const frame = window.requestAnimationFrame(() => {
      dialpadRef.current?.clearSearch();
      const active = document.activeElement;
      if (
        active instanceof HTMLElement &&
        active.closest(".mobileLanding-page__dialpad-chrome")
      ) {
        active.blur();
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isKeypadOpen]);
  const handleRecentsDetailOpenChange = useCallback((isOpen: boolean) => {
    setIsRecentsDetailOpen(isOpen);
    if (isOpen) {
      closeKeypad();
    }
  }, [closeKeypad]);
  const handleMissedDetailOpenChange = useCallback((isOpen: boolean) => {
    setIsMissedDetailOpen(isOpen);
    if (isOpen) {
      closeKeypad();
    }
  }, [closeKeypad]);
  const handleVoicemailDetailOpenChange = useCallback((isOpen: boolean) => {
    setIsVoicemailDetailOpen(isOpen);
    if (isOpen) {
      closeKeypad();
    }
  }, [closeKeypad]);

  useEffect(() => {
    if (!showFeatureTabs || !webexUserId) {
      setMyNumber("");
      return undefined;
    }

    setMyNumber("");
    let isMounted = true;

    const loadBusinessNumber = async () => {
      try {
        const response: IBusinessNumberResponse = await getBusinessNumber();
        const primaryNumber = pickDisplayBusinessPhone(response?.phoneNumbers);
        if (isMounted) {
          setMyNumber(primaryNumber?.value || "");
        }
      } catch {
        Logger.error(
          "MobileLandingPage#loadBusinessNumber() :: Unable to load business number"
        );
        if (isMounted) {
          setMyNumber("");
        }
      }
    };

    loadBusinessNumber();
    return () => {
      isMounted = false;
    };
  }, [showFeatureTabs, webexUserId]);

  useEffect(() => {
    const handleWebexInstanceTokenExpired = async () => {
      const tokenExpired = getLocalStorageItem<string>(
        VM_STORAGE_KEYS.WEBEX_INSTANCE_TOKEN_EXPIRED
      );
      if (!tokenExpired) {
        return;
      }

      removeLocalStorageItem(VM_STORAGE_KEYS.WEBEX_INSTANCE_TOKEN_EXPIRED);

      if (reInitInFlightRef.current) {
        Logger.info(
          "MobileLandingPage#handleWebexInstanceTokenExpired() :: recovery already in flight"
        );
        return;
      }

      const now = Date.now();
      if (now - lastReInitAtRef.current < MIN_REINIT_INTERVAL_MS) {
        Logger.info(
          "MobileLandingPage#handleWebexInstanceTokenExpired() :: recovery cooldown active"
        );
        return;
      }

      reInitInFlightRef.current = true;
      try {
        const oldToken = getLocalStorageItem<string>(WebexTokenEnum.AUTH_TOKEN);
        const tokens = await getWebexTokens(false);
        const newToken = tokens?.access_token;

        if (!newToken || newToken === oldToken) {
          Logger.info(
            "MobileLandingPage#handleWebexInstanceTokenExpired() :: token unchanged"
          );
          return;
        }

        if (newToken && !window.isWebexInitalized) {
          initWebex(newToken);
        }

        invalidateVoicemailClient();
        try {
          await whenDeviceRegistered(20_000);
        } catch (error) {
          Logger.info(
            `MobileLandingPage#handleWebexInstanceTokenExpired() :: device registration wait ended: ${formatError(error)}`
          );
        }

        voicemailRef.current?.refreshVoicemails();
        const refreshSharedCallHistory =
          callHistoryRef.current?.refreshCallHistory ??
          missedCallHistoryRef.current?.refreshCallHistory;
        refreshSharedCallHistory?.();
      } catch (error) {
        Logger.error(
          `MobileLandingPage#handleWebexInstanceTokenExpired() :: recovery failed: ${formatError(error)}`
        );
      } finally {
        lastReInitAtRef.current = Date.now();
        reInitInFlightRef.current = false;
      }
    };

    window.addEventListener("storage", handleWebexInstanceTokenExpired);
    return () => {
      window.removeEventListener("storage", handleWebexInstanceTokenExpired);
    };
  }, []);

  useEffect(() => {
    const loadTeamsContext = async () => {
      try {
        const teamsContext = await getMSTeamsContext();
        if (teamsContext?.app?.locale === LOCALIZATION_CODES.DE_DE) {
          setIsLocaleGerman(true);
        }
        if (teamsContext?.page?.subPageId === subPageId) {
          await pages.navigateToApp({
            appId: config.INTEGRATION_APP_ID || "",
            pageId: entityId,
          });
        }
      } catch (error) {
        Logger.error(
          `MobileLandingPage#loadTeamsContext() :: ${formatError(error)}`
        );
      }
    };
    loadTeamsContext();
  }, []);

  useEffect(() => {
    const handleVoicemailTab = () => {
      const isShowVMTabValue = getLocalStorageItem<string>(
        VM_STORAGE_KEYS.IS_HIDE_VM_TAB
      );
      setIsShowVMTab(!isShowVMTabValue);
    };
    handleVoicemailTab();
    window.addEventListener("storage", handleVoicemailTab);
    return () => {
      window.removeEventListener("storage", handleVoicemailTab);
    };
  }, []);

  useEffect(() => {
    const handleListErrors = () => {
      setVmListErrMsg(
        getLocalStorageItem<string>(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG) || ""
      );
      setChListErrMsg(
        getLocalStorageItem<string>(CH_STORAGE_KEYS.CH_ERROR_LIST_MSG) || ""
      );
    };
    handleListErrors();
    window.addEventListener("storage", handleListErrors);
    return () => {
      window.removeEventListener("storage", handleListErrors);
    };
  }, []);

  const dismissListError = (storageKey: string) => {
    removeLocalStorageItem(storageKey);
    window.dispatchEvent(new Event("storage"));
  };

  const handleVoicemailTabPress = () => {
    voicemailRef.current?.refreshVoicemails();
    callHistoryRef.current?.postMissedCallCount();
  };

  const vmCrossLaunchClick = () => {
    window.open(callVoicemailURL);
  };

  const makeCall = (address: string, video?: boolean, label?: string) => {
    if (!address) {
      return;
    }
    const telephone = address.replace(/ /g, "");
    window.open(buildMobileCallingUrl(telephone, video));
    submitMetrics({
      metricEvent: crossLaunchCallMetric,
      callMadeSection: label || MobileDialpad,
      callType: video ? videoCall : audioCall,
    });
  };

  const handleSearchInputChange = useCallback((value: string) => {
    if (value === "" && !isKeypadOpen) {
      suppressKeypadOpenUntilRef.current = Date.now() + 400;
    }
  }, [isKeypadOpen]);

  const openKeypad = useCallback(() => {
    if (Date.now() < suppressKeypadOpenUntilRef.current) {
      return;
    }
    setIsKeypadOpen(true);
  }, []);

  const dialpad = (
    <WebexMobileDialpad
      ref={dialpadRef}
      src={src}
      user={user}
      channelData={channelData}
      clientType={clientType}
      myNumber={showFeatureTabs ? myNumber : undefined}
      showOpenWebex={!showFeatureTabs}
      showKeypad={showFeatureTabs ? isKeypadOpen : true}
      keypadPortalTarget={showFeatureTabs ? keypadSlot : undefined}
      searchLabel={
        showFeatureTabs && isKeypadOpen
          ? t("mobile.typeOrDial", { defaultValue: "Type or Dial" })
          : undefined
      }
      onSearchInputChange={
        showFeatureTabs ? handleSearchInputChange : undefined
      }
      trailingAction={
        showFeatureTabs && !isKeypadOpen ? (
          <ButtonCircle
            outline
            ghost
            size={32}
            className="mobileLanding-page__keypad-toggle"
            onPress={openKeypad}
            aria-label={t("dialpad")}
            aria-pressed={false}
            data-testid="mobile-keypad-toggle"
          >
            <MobileKeypadToggleIcon />
          </ButtonCircle>
        ) : undefined
      }
    />
  );

  const featureTabs = [
    <TabBarItem
      key={WEBEX_TAB_ID.RECENT_CALLS}
      tabId={WEBEX_TAB_ID.RECENT_CALLS}
      label={t("recents")}
      onTabPress={() => {
        callHistoryRef.current?.refreshCallHistory();
      }}
    >
      {webexUserId ? (
        <WebexCallHistory
          key={`${webexUserId}-recents`}
          noHistoryMessage={t("calls.no_calling_history")}
          userID={webexUserId}
          ref={callHistoryRef}
          isLocaleGerman={isLocaleGerman}
          updateMissedCallCount={(unreadCount: number) =>
            setUnreadMissedCalls(unreadCount)
          }
          labelUnknown={t("calls.call_with_NoName_NoAddress") || "Unknown"}
          makeCall={makeCall}
          mobileLayout
          mobileFilter="all"
          hideMobileFilters
          onRestoreMobileFocus={focusActiveFeatureTab}
          onMobileDetailOpenChange={handleRecentsDetailOpenChange}
          presenceFeatureId="callHistoryMobileRecents"
        />
      ) : null}
    </TabBarItem>,
    <TabBarItem
      key={WEBEX_TAB_ID.MISSED}
      tabId={WEBEX_TAB_ID.MISSED}
      label={t("missed")}
      badgeCount={unreadMissedCalls}
      onTabPress={() => {
        missedCallHistoryRef.current?.refreshCallHistory();
      }}
    >
      {webexUserId ? (
        <WebexCallHistory
          key={`${webexUserId}-missed`}
          noHistoryMessage={t("calls.no_calling_history")}
          userID={webexUserId}
          ref={missedCallHistoryRef}
          isLocaleGerman={isLocaleGerman}
          labelUnknown={t("calls.call_with_NoName_NoAddress") || "Unknown"}
          makeCall={makeCall}
          mobileLayout
          mobileFilter="missed"
          hideMobileFilters
          onRestoreMobileFocus={focusActiveFeatureTab}
          onMobileDetailOpenChange={handleMissedDetailOpenChange}
          presenceFeatureId="callHistoryMobileMissed"
        />
      ) : null}
    </TabBarItem>,
  ];

  if (isShowVMTab) {
    featureTabs.push(
      <TabBarItem
        key={WEBEX_TAB_ID.VOICEMAIL}
        tabId={WEBEX_TAB_ID.VOICEMAIL}
        label={t("voicemail")}
        badgeCount={unreadVoicemails}
        onTabPress={handleVoicemailTabPress}
      >
        {webexUserId ? (
          <WebexVoicemail
            key={webexUserId}
            handleSetIsVmPlayErrorMsg={setIsVmPlayErrorMsg}
            updateUnreadCount={(unreadCount: number) =>
              setUnreadVoicemails(unreadCount)
            }
            ref={voicemailRef}
            isLocaleGerman={isLocaleGerman}
            userID={webexUserId}
            makeCall={makeCall}
            mobileLayout
            onMobileDetailOpenChange={handleVoicemailDetailOpenChange}
            onCallVoicemail={vmCrossLaunchClick}
          />
        ) : null}
      </TabBarItem>
    );
  }

  return (
    <div
      className={`mobileLanding-page${
        showFeatureTabs ? " mobileLanding-page--with-tabs" : ""
      }${
        showFeatureTabs && isKeypadOpen
          ? " mobileLanding-page--keypad-open"
          : ""
      }${
        showFeatureTabs && isRecordDetailOpen
          ? " mobileLanding-page--detail-open"
          : ""
      }`}
    >
      <section
        ref={tabBarContainerRef}
        className={`mobileLanding-page__content${
          showFeatureTabs ? " mobileLanding-page__content--tabs" : ""
        }`}
      >
        {showFeatureTabs && (
          <section role="status">
            {isVmPlayErrorMsg && (
              <CommonErrorAlert
                errorMessage={t("vmPlayErrorMessage")}
                onClose={() => setIsVmPlayErrorMsg(false)}
              />
            )}
            {vmListErrMsg && (
              <CommonErrorAlert
                errorMessage={t("errorListVM")}
                onClose={() =>
                  dismissListError(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG)
                }
              />
            )}
            {chListErrMsg && (
              <CommonErrorAlert
                errorMessage={t("errorListCH")}
                onClose={() =>
                  dismissListError(CH_STORAGE_KEYS.CH_ERROR_LIST_MSG)
                }
              />
            )}
          </section>
        )}
        {showFeatureTabs ? (
          <>
            <div className="mobileLanding-page__dialpad-chrome">
              {isKeypadOpen && (
                <div className="mobileLanding-page__keypad-toolbar">
                  <ButtonCircle
                    ghost
                    size={32}
                    className="mobileLanding-page__keypad-close"
                    onPress={closeKeypad}
                    aria-label={t("mobile.closeKeypad", {
                      defaultValue: "Close keypad",
                    })}
                    data-testid="mobile-keypad-close"
                  >
                    <MobileKeypadCloseIcon />
                  </ButtonCircle>
                  <ButtonCircle
                    ghost
                    size={32}
                    className="mobileLanding-page__keypad-keyboard"
                    onPress={() => dialpadRef.current?.focusSearch()}
                    aria-label={t("mobile.useKeyboard", {
                      defaultValue: "Use keyboard",
                    })}
                    data-testid="mobile-keypad-keyboard"
                  >
                    <MobileKeypadKeyboardIcon />
                  </ButtonCircle>
                </div>
              )}
              {dialpad}
            </div>
            <div className="mobileLanding-page__lists">
              <TabBar
                appearance="mobilePill"
                defaultActiveTabs={[WEBEX_TAB_ID.RECENT_CALLS]}
              >
                {featureTabs}
              </TabBar>
              <div
                ref={setKeypadSlot}
                className="mobileLanding-page__keypad-slot"
                data-testid="mobile-keypad-slot"
              />
            </div>
          </>
        ) : (
          dialpad
        )}
      </section>
    </div>
  );
};
